import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import {
  MapPin,
  CalendarCheck,
  BellRinging,
  Storefront,
  ImageSquare,
  Code,
  EnvelopeSimple,
  ChartBar,
  ArrowRight,
} from "@phosphor-icons/react";

const BASE = "https://thetrucktrackr.com";

const FEATURES = [
  {
    icon: MapPin,
    title: "Live map pin",
    pro: false,
    body: "Post your spot and you're on the live customer map instantly — with hours, address, and one-tap directions.",
  },
  {
    icon: CalendarCheck,
    title: "Schedules that repeat",
    pro: false,
    body: "Post one-off events or a recurring weekly schedule. Private catering and closed days stay off the public map automatically.",
  },
  {
    icon: BellRinging,
    title: "Follower push notifications",
    pro: false,
    body: "Customers follow your truck and get a push notification when you post — and when you're serving nearby.",
  },
  {
    icon: EnvelopeSimple,
    title: "Event booking leads",
    pro: false,
    body: "Local event hosts request trucks through TruckTrackr. Matching inquiries land straight in your dashboard inbox.",
  },
  {
    icon: Storefront,
    title: "Public SEO profile",
    pro: false,
    body: "Your own page at thetrucktrackr.com/t/your-truck with logo, cover photo, description, and your upcoming schedule — indexed by Google.",
  },
  {
    icon: ImageSquare,
    title: "Post Studio",
    pro: true,
    body: "Turn your week's schedule into a polished social media graphic using your own photos, logo, and brand colors. Ready every Sunday.",
  },
  {
    icon: CalendarCheck,
    title: "Calendar sync",
    pro: true,
    body: "Two-way sync with Google Calendar and Outlook, plus an Apple Calendar feed for staff. Plan in your calendar — the map updates itself.",
  },
  {
    icon: Code,
    title: "Website embed widget",
    pro: true,
    body: "Drop your live weekly schedule onto your own website with one snippet. Update once, current everywhere.",
  },
  {
    icon: ChartBar,
    title: "Insights & analytics",
    pro: true,
    body: "See profile views, follower growth, and which events pull the most attention.",
  },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "TruckTrackr Features for Food Truck Owners",
  url: `${BASE}/features`,
};

export default function Features() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16" data-testid="features-page">
      <PageMeta
        title="Food Truck Owner Features — Map, Schedules & Post Studio | TruckTrackr"
        description="Everything TruckTrackr gives food truck owners: live map pin, recurring schedules, follower push alerts, booking leads, calendar sync, website embed, and AI-assisted schedule graphics."
        canonical={`${BASE}/features`}
        jsonLd={JSON_LD}
      />

      <div className="max-w-2xl">
        <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">For truck owners</span>
        <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl sm:text-5xl">
          Post once. <span className="text-primary">Be everywhere.</span>
        </h1>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          One schedule powers your map pin, your public profile, your followers'
          notifications, your social posts, and your website. Here's what's inside.
        </p>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            className="rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-colors"
            data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="flex items-center justify-between">
              <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center">
                <f.icon weight="duotone" className="h-5 w-5" />
              </div>
              {f.pro && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2 py-0.5">
                  Pro
                </span>
              )}
            </div>
            <h2 className="mt-4 font-display font-semibold text-base md:text-lg tracking-tight">{f.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-10 text-center">
        <h2 className="font-display font-bold text-lg md:text-lg">Get your truck on the map in under a minute.</h2>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup?role=owner" data-testid="features-cta-signup">
            <Button size="lg" className="gap-2 h-12 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
              <Storefront weight="duotone" className="h-5 w-5" />
              Sign up free
            </Button>
          </Link>
          <Link to="/pricing" data-testid="features-cta-pricing">
            <Button size="lg" variant="outline" className="gap-2 h-12 px-6 rounded-full w-full sm:w-auto">
              See pricing
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
